// Shared paper editor used by the My papers page and the legacy dashboard link.
// Manual drafts, PDF extraction and "From your content" all land in this same
// editor, so a teacher has one place to review, add, fix, enrich and publish a
// paper. Each question is edited by `PaperItemEditor` — TEAM, time, reading
// window, word limit, maths preview, video and a concept tag — shared with the
// Author page; questions can be pulled from the bank; and the paper prints as a
// question paper or an OMR sheet.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TEAM_PART_LABEL, paperCodeFor, paperSeconds, schemeTotal } from '@cwy/config/contracts';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { useAccount } from '@/hooks/useSession';
import type {
  ConceptOption,
  ConceptTagResult,
  CustomPaperDetail,
  CustomPaperItem,
  CustomPaperListItem,
  TeamPart,
} from '@/types';
import { PaperItemEditor } from '@/components/teacher/paper/PaperItemEditor';
import { OriginalPdfButton } from '@/components/teacher/OriginalPdfButton';
import { useTeacherYear, withYear } from '@/hooks/useTeacherYear';
import { QuestionBankPicker } from '@/components/teacher/paper/QuestionBankPicker';
import { OmrSheet, PrintablePaper, type PrintHeader } from '@/components/teacher/paper/PrintSheets';
import {
  bankToItem,
  defaultItem,
  fromServer,
  messageFor,
  serializeItems,
  type PaperQuestionType,
} from '@/components/teacher/paper/editorItems';

interface Props {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Render only the editor, for `/teacher/papers/:id/edit`. */
  standalone?: boolean;
  onBack?: () => void;
}

const STATUS_BADGE: Record<string, { text: string; cls: string }> = {
  draft: { text: 'Draft', cls: 'bg-slate-100 text-slate-700' },
  processing: { text: 'Reading paper…', cls: 'bg-amber-50 text-amber-700' },
  review: { text: 'Ready to review', cls: 'bg-indigo-50 text-indigo-700' },
  done: { text: 'Ready to review', cls: 'bg-indigo-50 text-indigo-700' },
  publishing: { text: 'Publishing…', cls: 'bg-amber-50 text-amber-700' },
  published: { text: 'Published', cls: 'bg-green-50 text-green-700' },
  failed: { text: 'Failed', cls: 'bg-red-50 text-red-700' },
};

const isReviewable = (status: string) =>
  status === 'draft' || status === 'review' || status === 'done';

function detailState(
  detail: CustomPaperDetail | undefined,
  detailQuery: { isLoading: boolean },
  children: ReactNode,
) {
  if (detailQuery.isLoading) return <div className="h-32 animate-pulse rounded-2xl bg-slate-100" />;
  if (!detail)
    return <p className="py-10 text-center text-sm text-slate-500">Couldn’t load this paper.</p>;
  if (detail.status === 'processing') {
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-line bg-card py-14 text-sm text-slate-500">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-300 border-t-forest" />
        AI is reading and solving “{detail.title}”…
      </div>
    );
  }
  if (detail.status === 'failed') {
    return (
      <p className="rounded-2xl bg-red-50 p-5 text-sm text-red-700">
        {detail.error ?? 'This paper could not be solved.'}
      </p>
    );
  }
  return children;
}

export function CustomPaperPanel({ selectedId, onSelect, standalone = false, onBack }: Props) {
  const { yearParam } = useTeacherYear();
  const listQuery = useQuery({
    queryKey: ['custom-papers', yearParam],
    queryFn: () => apiGet<CustomPaperListItem[]>(withYear('/teacher/custom-papers', yearParam)),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((paper) => paper.status === 'processing') ? 5_000 : false,
  });

  const detailQuery = useQuery({
    queryKey: ['custom-paper', selectedId],
    enabled: Boolean(selectedId),
    queryFn: () => apiGet<CustomPaperDetail>(`/teacher/custom-papers/${selectedId}`),
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 3_000 : false),
  });

  const detail = detailQuery.data;

  if (standalone) {
    return detailState(
      detail,
      detailQuery,
      detail ? (
        <CustomPaperReview key={detail.id} detail={detail} standalone onBack={onBack} />
      ) : null,
    );
  }

  const papers = listQuery.data ?? [];
  return (
    <div className="rounded-2xl border border-line bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink">My papers</h3>

      {papers.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">No papers yet.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-[250px_1fr]">
          <ul className="space-y-1">
            {papers.map((paper) => {
              const badge = STATUS_BADGE[paper.status] ?? STATUS_BADGE.draft;
              const active = paper.id === selectedId;
              return (
                <li key={paper.id}>
                  <button
                    onClick={() => onSelect(paper.id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${
                      active ? 'bg-forest/10 ring-1 ring-forest/30' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className="min-w-0 truncate font-medium text-slate-700">
                      {paper.title}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-fs-10 font-semibold ${badge.cls}`}
                    >
                      {badge.text}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="min-w-0">
            {!selectedId ? (
              <p className="py-10 text-center text-sm text-slate-500">Select a paper to edit it.</p>
            ) : (
              detailState(
                detail,
                detailQuery,
                detail ? <CustomPaperReview key={detail.id} detail={detail} /> : null,
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomPaperReview({
  detail,
  standalone = false,
  onBack,
}: {
  detail: CustomPaperDetail;
  standalone?: boolean;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<CustomPaperItem[]>(() => fromServer(detail.items));
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [bankOpen, setBankOpen] = useState(false);

  // Local state only — re-seeding the editor from a refetch, never a write.
  useEffect(() => {
    if (!dirty) setItems(fromServer(detail.items));
  }, [detail.items, dirty]);

  const conceptsQuery = useQuery({
    queryKey: ['concepts', detail.subjectId],
    enabled: Boolean(detail.subjectId),
    queryFn: () => apiGet<ConceptOption[]>(`/teacher/concepts?subjectId=${detail.subjectId}`),
  });
  const concepts = useMemo(() => conceptsQuery.data ?? [], [conceptsQuery.data]);

  // A previous year's paper is history: it opens and prints, and nothing on
  // it changes. The server refuses the writes too (409); this only stops the
  // editor offering them.
  const editable = isReviewable(detail.status) && !detail.readOnly;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['custom-papers'] });
    void queryClient.invalidateQueries({ queryKey: ['custom-paper', detail.id] });
    void queryClient.invalidateQueries({ queryKey: ['teacher-papers'] });
  };

  /** Anything unsaved goes up first, so the server reads what is on the screen. */
  const flush = async () => {
    if (!dirty) return;
    await apiPatch(`/teacher/custom-papers/${detail.id}`, { items: serializeItems(items) });
    setDirty(false);
  };

  const save = useMutation({
    mutationFn: () =>
      apiPatch<CustomPaperDetail>(`/teacher/custom-papers/${detail.id}`, {
        items: serializeItems(items),
      }),
    onSuccess: (saved) => {
      setItems(fromServer(saved.items));
      setDirty(false);
      setMessage('Draft saved.');
      setError('');
      invalidate();
    },
    onError: (err: unknown) => setError(messageFor(err)),
  });

  const publish = useMutation({
    mutationFn: async () => {
      await flush();
      return apiPost<{ paperId: string }>(`/teacher/custom-papers/${detail.id}/publish`, {});
    },
    onSuccess: () => {
      setMessage('Published. Students in this class can now attempt the paper.');
      setError('');
      invalidate();
    },
    onError: (err: unknown) => setError(messageFor(err)),
  });

  const generate = useMutation({
    mutationFn: async (vars: { index: number; parts: TeamPart[]; mode: 'fill' | 'rewrite' }) => {
      await flush();
      return apiPost<CustomPaperDetail>(
        `/teacher/custom-papers/${detail.id}/generate-content`,
        vars,
      );
    },
    onSuccess: (generated, vars) => {
      setItems(fromServer(generated.items));
      setMessage(
        `Q${vars.index}: AI ${vars.mode === 'rewrite' ? 'rewrote' : 'wrote'} ${vars.parts
          .map((p) => TEAM_PART_LABEL[p])
          .join(', ')}. It is marked as a draft until you edit it — read it before publishing.`,
      );
      setError('');
      invalidate();
    },
    onError: (err: unknown) => setError(messageFor(err)),
  });

  const tag = useMutation({
    mutationFn: async (index: number) => {
      await flush();
      return apiPost<CustomPaperDetail & { tag: ConceptTagResult }>(
        `/teacher/custom-papers/${detail.id}/tag`,
        { index },
      );
    },
    onSuccess: (tagged, index) => {
      setItems(fromServer(tagged.items));
      setMessage(
        !tagged.tag
          ? `Q${index}: nothing in the question names a sub-topic in this subject — pick one with ✎ Change.`
          : tagged.tag.sure
            ? `Q${index} re-read and tagged ${tagged.tag.name} · ${tagged.tag.chapterName}.`
            : `Q${index}: tagged ${tagged.tag.name} · ${tagged.tag.chapterName} on one shared word — a best guess, check it.`,
      );
      setError('');
      invalidate();
    },
    onError: (err: unknown) => setError(messageFor(err)),
  });

  /** Editing a part the AI wrote makes it the teacher's — the draft flag comes off. */
  const patchItem = (index: number, patch: Partial<CustomPaperItem>, part?: TeamPart) => {
    setItems((current) =>
      current.map((item) =>
        item.index === index
          ? {
              ...item,
              ...patch,
              aiDrafted: part ? (item.aiDrafted ?? []).filter((p) => p !== part) : item.aiDrafted,
            }
          : item,
      ),
    );
    setDirty(true);
    setMessage('');
  };

  const changeType = (index: number, type: PaperQuestionType) => {
    patchItem(index, {
      type,
      options: type === 'MCQ' ? ['', '', '', ''] : null,
      correctOptionIdx: null,
    });
  };

  const addQuestion = () => {
    setItems((current) => [...current, defaultItem(current.length + 1)]);
    setDirty(true);
    setMessage('');
  };

  const removeQuestion = (index: number) => {
    setItems((current) =>
      current.filter((item) => item.index !== index).map((item, i) => ({ ...item, index: i + 1 })),
    );
    setDirty(true);
    setMessage('');
  };

  // What a Print shows. Rendered only for the print, synchronously, so there is
  // no effect and no stale sheet left in the DOM for the next one.
  const [printing, setPrinting] = useState<'paper' | 'omr' | null>(null);
  const account = useAccount(true);
  const printHeader: PrintHeader = {
    schoolName: account.data?.tenant?.name ?? 'CWY',
    title: detail.title,
    classroomName: detail.classroomName ?? '',
    subjectName: detail.subjectName ?? '',
    // The published paper's code once there is one — the code the class report
    // carries — so a scanned sheet and its report agree.
    paperCode: paperCodeFor(detail.publishedPaperId ?? detail.id),
  };
  const print = (kind: 'paper' | 'omr') => {
    flushSync(() => setPrinting(kind));
    window.print();
    setPrinting(null);
  };

  const inPaper = useMemo(
    () => new Set(items.map((i) => i.bankId).filter((id): id is string => Boolean(id))),
    [items],
  );

  const busy = save.isPending || publish.isPending || generate.isPending || tag.isPending;
  const totalMarks = items.reduce((sum, i) => sum + (i.marks ?? 0), 0);
  const totalMinutes = Math.round(paperSeconds(items) / 60);

  const confirmPublish = () => {
    // A scheme that does not total its question is the first thing a teacher
    // checks on a marked script — say so before students sit it, not after.
    const off = items.filter(
      (i) => (i.markingScheme?.length ?? 0) > 0 && schemeTotal(i.markingScheme) !== (i.marks ?? 0),
    );
    // The hint is the first lifeline; without it a student gets a stock line
    // on attempts 2 and 3 instead of help.
    const hintless = items.filter((i) => !i.lightHint?.trim());
    const warning = [
      off.length
        ? `The marking scheme does not total the marks on ${off.map((i) => `Q${i.index}`).join(', ')}.`
        : '',
      hintless.length
        ? `${hintless.map((i) => `Q${i.index}`).join(', ')} ${hintless.length === 1 ? 'has' : 'have'} no hint — “Generate TEAM with AI” can write one.`
        : '',
    ]
      .filter(Boolean)
      .map((line) => `\n\n${line}`)
      .join('');
    if (
      window.confirm(
        `Publish this paper? Students in the selected class will be able to attempt it.${warning}`,
      )
    ) {
      publish.mutate();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {standalone && onBack && (
            <button
              onClick={onBack}
              className="mb-2 text-xs font-semibold text-slate-500 hover:text-forest"
            >
              ← My papers
            </button>
          )}
          <h1 className="text-xl font-bold text-ink">{detail.title}</h1>
          <p className="mt-1 text-xs text-slate-500">
            {detail.classroomName ?? 'Class not selected'} ·{' '}
            {detail.subjectName ?? 'Subject not selected'} · {detail.board} · Grade{' '}
            {detail.grade === 'TEN' ? '10' : '12'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {detail.hasOriginal && (
            <OriginalPdfButton
              customPaperId={detail.id}
              className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-paper-sunk disabled:opacity-50"
            />
          )}
          {(['paper', 'omr'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              disabled={items.length === 0}
              onClick={() => print(kind)}
              className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-paper-sunk disabled:opacity-50"
            >
              {kind === 'paper' ? 'Print paper' : 'Print OMR sheet'}
            </button>
          ))}
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${(STATUS_BADGE[detail.status] ?? STATUS_BADGE.draft).cls}`}
          >
            {(STATUS_BADGE[detail.status] ?? STATUS_BADGE.draft).text}
          </span>
        </div>
      </div>

      {message && (
        <p className="rounded-lg bg-green-50 px-3 py-2 text-xs font-medium text-green-700">
          {message}
        </p>
      )}
      {error && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>
      )}

      {detail.readOnly && (
        <p className="rounded-xl border border-line bg-paper-sunk px-3 py-2 text-xs text-slate-600">
          This paper belongs to a previous academic year, so it is read-only. Print it, or download
          the PDF it was uploaded as.
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-card p-3">
          <p className="mr-auto text-xs text-slate-500">
            {items.length} question{items.length === 1 ? '' : 's'} · {totalMarks} marks · about{' '}
            {totalMinutes} min · Save keeps this paper as a draft.
          </p>
          <button
            onClick={addQuestion}
            disabled={busy}
            className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-paper-sunk disabled:opacity-50"
          >
            + Add question
          </button>
          <button
            onClick={() => setBankOpen(true)}
            disabled={busy || !detail.subjectId}
            className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-paper-sunk disabled:opacity-50"
          >
            + Add from bank
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || busy}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save draft'}
          </button>
          <button
            onClick={confirmPublish}
            disabled={busy || items.length === 0}
            className="rounded-lg bg-forest px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {publish.isPending ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      )}

      {!editable && detail.status === 'published' && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3">
          <span className="text-xs font-semibold text-green-700">Live for students</span>
          {detail.publishedPaperId && (
            <button
              onClick={() =>
                navigate(
                  `/teacher/analytics?paperId=${encodeURIComponent(detail.publishedPaperId!)}`,
                )
              }
              className="ml-auto rounded-lg border border-green-300 bg-white px-3 py-1.5 text-xs font-semibold text-green-800 hover:bg-green-100"
            >
              View analytics →
            </button>
          )}
        </div>
      )}

      {editable && (
        <p className="text-xs text-slate-500">
          Each question carries its TEAM — Tips, the Explanation, the Answer with its marking
          scheme, and common Mistakes. “Generate TEAM with AI” fills what is blank and leaves what
          you wrote alone; ↻ on a part rewrites just that part. Everything it writes stays editable,
          and is marked as a draft until you touch it.
        </p>
      )}

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-card py-12 text-center">
          <p className="text-sm font-semibold text-ink">This draft is empty.</p>
          <p className="mt-1 text-xs text-slate-500">
            Add a question to start building the paper, or pull some from the bank.
          </p>
        </div>
      ) : (
        items.map((item) => (
          <PaperItemEditor
            key={item.index}
            idPrefix={detail.id}
            item={item}
            concepts={concepts}
            editable={editable}
            busy={busy}
            generating={generate.isPending && generate.variables?.index === item.index}
            tagging={tag.isPending && tag.variables === item.index}
            onPatch={patchItem}
            onTypeChange={changeType}
            onGenerate={(parts, mode) => generate.mutate({ index: item.index, parts, mode })}
            onTag={() => tag.mutate(item.index)}
            onRemove={() => removeQuestion(item.index)}
          />
        ))
      )}

      {/* Outside #root, so the print rules can drop the whole app from the
          page flow — see the @media print block in globals.css. */}
      {printing === 'paper' &&
        createPortal(<PrintablePaper header={printHeader} items={items} />, document.body)}
      {printing === 'omr' &&
        createPortal(<OmrSheet header={printHeader} items={items} />, document.body)}

      {bankOpen && detail.subjectId && (
        <QuestionBankPicker
          subjectId={detail.subjectId}
          subjectName={detail.subjectName ?? 'This subject'}
          inPaper={inPaper}
          onClose={() => setBankOpen(false)}
          onAdd={(b) => {
            setItems((current) => [...current, bankToItem(b, current.length + 1)]);
            setDirty(true);
            setMessage(`Added from the bank: ${b.subtopicName}. Save to keep it.`);
          }}
        />
      )}
    </div>
  );
}
